import { useEffect, useState } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import http from "@/lib/api";
import { LayoutDashboard, ClipboardList, Users, Wrench, Menu, X, LogOut, Settings, Bot, CalendarDays, CalendarX, ScrollText, Mail, History, Brain, MessageSquare, AudioLines, Calculator, FileText, Bell, ScanLine, Cpu, UserCog, PhoneCall } from "lucide-react";
import { enablePushNotifications } from "@/lib/push";
import { useNoIndex } from "@/lib/useNoIndex";

const navItems = [
  { path: "/admin/dashboard", label: "Панель", icon: LayoutDashboard },
  { path: "/admin/skann", label: "Сканировать", icon: ScanLine, both: true },
  { path: "/admin/foresporler", label: "Запросы", icon: ClipboardList },
  { path: "/admin/innleveringer", label: "Заявки на сдачу", icon: FileText },
  { path: "/admin/ai-requests", label: "AI-запросы", icon: Bot },
  { path: "/admin/svyazatsya", label: "Связаться с клиентом", icon: PhoneCall, both: true },
  { path: "/admin/bestillinger", label: "Бронирования", icon: CalendarDays },
  { path: "/admin/raspisanie", label: "Доступность", icon: CalendarX },
  { path: "/admin/tseny", label: "Цены и часы", icon: Calculator },
  { path: "/admin/kunder", label: "Клиенты", icon: Users },
  { path: "/admin/reparasjoner", label: "Ремонтные заказы", icon: Wrench, both: true },
  { path: "/admin/mashiny", label: "Машины", icon: Cpu, both: true },
  { path: "/admin/aktivitetslogg", label: "Журнал активности", icon: ScrollText },
  { path: "/admin/shablony-email", label: "Шаблоны email", icon: Mail },
  { path: "/admin/istoriya-uvedomleniy", label: "История уведомлений", icon: History },
  { path: "/admin/baza-znaniy", label: "База знаний", icon: Brain },
  { path: "/admin/kommunikasjon", label: "Коммуникация", icon: MessageSquare },
  { path: "/admin/agent-vadim", label: "AI-агент Vadim", icon: AudioLines },
  { path: "/admin/regnskap", label: "Бухгалтерия", icon: Calculator },
  { path: "/admin/polzovateli", label: "Пользователи", icon: UserCog },
  { path: "/admin/innstillinger", label: "Настройки", icon: Settings },
];

export default function AdminLayout() {
  useNoIndex();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminName, setAdminName] = useState("");
  const [verified, setVerified] = useState(false);
  const [role, setRole] = useState("admin");

  useEffect(() => {
    const verify = async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) { navigate("/admin/login"); return; }
      try {
        const r = await http.get("/v1/admin/verify");
        setAdminName(localStorage.getItem("admin_name") || "Admin");
        const rl = r.data.role || "admin";
        setRole(rl);
        localStorage.setItem("admin_role", rl);
        if (r.data.admin_id) localStorage.setItem("admin_id", r.data.admin_id);
        setVerified(true);
      } catch {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_name");
        navigate("/admin/login");
      }
    };
    verify();
  }, [navigate]);

  const handleLogout = async () => {
    try { await http.post("/v1/admin/logout"); } catch { /* ignore */ }
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_id");
    navigate("/admin/login");
  };

  if (!verified) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900"><p className="text-slate-400">Проверка...</p></div>;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-200 overflow-y-auto ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-4 border-b border-slate-700"><h1 className="text-lg font-bold text-white">BKTS Admin</h1><p className="text-sm text-slate-400">{role === "workshop" ? "Приёмка / Verksted" : "Bergen Serviceverksted"}</p></div>
        <nav className="p-4 space-y-1">
          {navItems.filter((item) => role === "admin" || item.both).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`admin-nav-${item.path.split("/").pop()}`} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700 hover:text-white"}`}>
                <Icon className="h-4 w-4" />{item.label}
              </Link>
            );
          })}
        </nav>
      </aside>
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4">
          <Button variant="ghost" size="icon" className="lg:hidden text-slate-300 hover:text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</Button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={enablePushNotifications} className="text-slate-300 hover:text-white" data-testid="admin-enable-push" title="Aktiver push-varsler"><Bell className="h-4 w-4 mr-1" />Уведомления</Button>
            <span className="text-sm text-slate-300">{adminName}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-300 hover:text-white" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1" />Выход</Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}
